/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

//constants
import { FIELDS } from "constants/PostFormFields";
import { PLATFORMS, PlatformSpec } from "constants/Platforms";
import { CHARACTER_LIMITS } from "constants/CharacterLimits";

//helpers
import { sortAccounts } from "utils/accounts";
import { countCharacter } from "utils/strings";

//components
import { Toggle, TooltipWrapper } from "ui";

//actions
import { togglePlatformLink, updatePostFormField } from "actions/postForm";

//selectors
import { getLoading, getUser } from "selectors/commonSelectors";

import CaptionField from "components/Posts/PostForm/PostCaption/CaptionField";
import { useTrackEvent } from "libs/analytics";

import { AltTextModal } from "../modals/alt-text";
import GmbFields from "../GmbFields/GmbFields";
import YtFields from "../YouTubeFields/YouTubeFields";
import PinterestFields from "../PinterestFields/PinterestFields";
import { PlatformUsage, PostFirstComment, PostTags, PostUrl } from "../index";
import { PlatformFieldsOverlay } from "./PlatformFieldsOverlay";
import { UNLINK_TOGGLE_TW } from "constants/Events";
import { TiktokSwitchNotification } from "../TiktokFields/TiktokSwitchNotification";
import { ThreadsSwitchNotification } from "../threadsFields/ThreadsSwitchNotification";
import { TiktokSwitchAllows } from "../TiktokFields/TiktokSwitchAllows";
import { PostAlerts } from "./PostAlerts";
import { getLinkedInAccountDetail } from "./single-post-form";
import ThreadsReplyControl from "../threadsFields/ThreadsReplyControl";
import { useVideoCover } from "./VideoCoverSelector/hook";
import { VideoCoverSelector } from "./VideoCoverSelector/VideoCoverSelector";
import { VideoReelToggle } from "./VideoReelToggle/VideoReelToggle";
import { Post } from "../../../../features/post";
import {
  FormPlatformType,
  FormValidationErrors,
  PlatformFields,
} from "../../../../shared/types/post";
import { AccountType } from "../../../../shared/types/accounts";
import { BulkTabContext } from "../types";
import { SkedEditorHistory } from "../../../../ui/SkedCaption/v2/sked-caption-v2";

export interface ClusterPostFormProps {
  post: Post;
  postFormValue: Post;
  errors: FormValidationErrors | undefined;
  platforms: PlatformSpec[];
  platformFields: PlatformFields;
  formAccounts: AccountType[];
  updateFields: (fields: Record<string, unknown>) => void;
  updateField: (field: string, value: unknown) => void;
  platformPost: any;
  selectedPlatform: FormPlatformType;
  visibleFields: string[];
  isSummary?: boolean;
  hasPostTags?: boolean;
  hasTikTokPosting: boolean;
  oldTiktokAccounts: AccountType[];
  ttIsNotification: boolean;
  hasThreadsPosting: boolean;
  oldThreadsAccounts: AccountType[];
  thIsNotification: boolean;
  showUrlField: boolean;
  bulkTab: BulkTabContext | undefined;
  onCoverPreviewChange?: (coverUrl: string | null) => void;
  captionEditHistory?: SkedEditorHistory;
}

export function ClusterPostForm({
  post,
  postFormValue,
  errors,
  platforms,
  platformFields,
  formAccounts,
  updateFields,
  updateField,
  platformPost,
  selectedPlatform,
  visibleFields,
  isSummary,
  hasPostTags,
  hasTikTokPosting,
  oldTiktokAccounts,
  ttIsNotification,
  hasThreadsPosting,
  oldThreadsAccounts,
  thIsNotification,
  showUrlField,
  bulkTab,
  onCoverPreviewChange,
  captionEditHistory,
}: ClusterPostFormProps) {
  const dispatch = useDispatch();
  const [openAddAlt, setOpenAddAlt] = useState(false);

  const isLoading = useSelector(getLoading);
  // Warning: If we are using this for creat single post we need to make sure user is initialized properly.
  const trackEvent = useTrackEvent();

  const fieldsRef = useRef(null);
  const user = useSelector(getUser);
  const sortedAccounts = sortAccounts(Object.values(formAccounts || []));

  const allDisabled = sortedAccounts.length === 0 || isLoading || post.past;

  const disabledWhenNotUnlinked =
    selectedPlatform !== "LINKED" &&
    (!platformFields[selectedPlatform] ||
      platformFields[selectedPlatform].unlinked !== true);

  const platformFormValue = useMemo(
    () => ({
      ...postFormValue,
      ...(selectedPlatform !== "LINKED" && platformFields[selectedPlatform]),
      platformType: selectedPlatform,
    }),
    [platformFields, postFormValue, selectedPlatform]
  );

  const {
    showVideoSelector,
    isLoading: isLoadingVideo,
    videoMedia,
    videoUrl,
    thumbnails,
    thumbnailUrl,
    videoLoadingError,
    originalMediaUrl,
  } = useVideoCover({
    selectedPlatform,
    platforms: platforms,
    platformPost,
    thumbCount: bulkTab ? 6 : 10,
  });

  useEffect(() => {
    if (!showVideoSelector) {
      onCoverPreviewChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showVideoSelector]);

  if (!platformPost) {
    return "";
  }
  let captionHashtagCount = countCharacter(
    platformFormValue?.caption ?? "",
    "#"
  );
  let firstCommentHashtagCount = countCharacter(
    platformFormValue?.firstcomment ?? "",
    "#"
  );
  const hasInsta = sortedAccounts.find(
    (account) => account.platformName === "instagram"
  );

  if (hasInsta) {
    captionHashtagCount = firstCommentHashtagCount =
      captionHashtagCount + firstCommentHashtagCount;
  }

  const hasYT = platforms.some((platform) => platform.TYPE === "YT");

  const captionMaxLength = getCaptionLengthLimit(
    platformFormValue,
    platforms.filter(
      (platform) => platformFields[platform.TYPE]?.unlinked !== true
    )
  );

  function handleMediaUpdate(mediaFiles) {
    updateField(FIELDS.MEDIA, mediaFiles);
  }

  function handleImageAltUpdate(value) {
    updateField(FIELDS.ALT_TEXT, value);
  }

  function handleOpenAddAlt() {
    setOpenAddAlt(!openAddAlt);
  }

  const onCaptionFieldChange = (val) => {
    updateField(FIELDS.CAPTION, val);
  };

  const isTwitter = selectedPlatform === "TW";

  const { hasMoreThanOnelinkedInAccount, selectedLinkedInAccountType } =
    getLinkedInAccountDetail(postFormValue.accounts);

  // @codeme Break this up into smaller components
  return (
    <PostFormProvider id={platformFormValue._id} disabled={allDisabled}>
      {isTwitter ? (
        <TwitterClusterForm
          post={platformPost}
          unlinked={platformFields?.TW?.unlinked}
          toggleUnlinked={() => {
            trackEvent({ eventName: UNLINK_TOGGLE_TW });
            dispatch(togglePlatformLink(bulkTab?.formId));
          }}
          errors={errors}
          platforms={platforms}
          formId={bulkTab?.formId}
          captionEditHistory={captionEditHistory}
          captionHashtagCount={captionHashtagCount}
        />
      ) : (
        <>
          {/* Platform post disconnection toggle */}
          {selectedPlatform !== "LINKED" && (
            <div className="flex flex-col mb-4">
              <div className="flex items-center justify-start mb-2">
                <TooltipWrapper
                  tooltip={`Custom changes for ${PLATFORMS[selectedPlatform].LABEL} will apply to all ${PLATFORMS[selectedPlatform].LABEL} accounts that you have selected.`}
                >
                  <div className="text-base mr-2 font-medium">
                    Customise {PLATFORMS[selectedPlatform].LABEL} post
                  </div>
                  <Toggle
                    key={`unlink-toggle-${selectedPlatform}`}
                    defaultChecked={Boolean(
                      platformFields[selectedPlatform] &&
                        platformFields[selectedPlatform].unlinked === true
                    )}
                    icons={{
                      unchecked: null,
                    }}
                    className="active-purple"
                    onChange={() => {
                      trackEvent({
                        eventName: `unlink-toggle-${selectedPlatform}`,
                      });
                      dispatch(togglePlatformLink(bulkTab?.formId));
                    }}
                  />
                </TooltipWrapper>
              </div>
            </div>
          )}

          {/* Wrapper for platform fields */}
          <div
            ref={fieldsRef}
            className="relative"
            key={`fields-${selectedPlatform}-${
              !platformFields[selectedPlatform] ||
              platformFields[selectedPlatform].unlinked !== true
                ? "default"
                : "custom"
            }`}
          >
            {/* Overlay for a linked platform */}
            {disabledWhenNotUnlinked && (
              <PlatformFieldsOverlay fieldsRef={fieldsRef} />
            )}

            <PostAlerts
              postFormValue={platformFormValue}
              oldTiktokAccounts={oldTiktokAccounts}
              platforms={platforms}
              selectedPlatform={selectedPlatform}
              ttIsNotification={ttIsNotification}
              isThreadsPostingEnabled={true}
              oldThreadsAccounts={oldThreadsAccounts}
              thIsNotification={thIsNotification}
            />

            <div>
              {/* Story Toggle - disabled version for when viewing with linked */}
              {/* This is so that customers can 'see' that we support these posts */}
              {selectedPlatform === "LINKED" &&
                platforms.length > 1 &&
                platforms.some((platform) => platform.TYPE === "IG") && (
                  <div className="inline-block mb-2">
                    <TooltipWrapper
                      placement={"top"}
                      tooltip={
                        "To post an Instagram story or send a notification, remove non-Instagram or notification channels."
                      }
                    >
                      <div className="flex items-center justify-start">
                        <div className="text-base mr-2 font-medium text-gray-700">
                          Switch to Instagram Story or Notification Post
                        </div>
                        <Toggle
                          key={`story-toggle-${selectedPlatform}`}
                          defaultChecked={
                            postFormValue[FIELDS.IS_STORY] || false
                          }
                          icons={{
                            unchecked: null,
                          }}
                          className="active-purple"
                          disabled={true}
                        />
                      </div>
                    </TooltipWrapper>
                  </div>
                )}
              {selectedPlatform === "TT" && (
                <TiktokSwitchNotification
                  platforms={platforms}
                  value={ttIsNotification}
                  hasTikTokPosting={hasTikTokPosting}
                  updateField={updateField}
                  allDisabled={allDisabled || disabledWhenNotUnlinked}
                  oldTiktokAccounts={oldTiktokAccounts}
                />
              )}
              {selectedPlatform === "TH" && (
                <ThreadsSwitchNotification
                  platforms={platforms}
                  value={thIsNotification}
                  hasThreadsPosting={hasThreadsPosting}
                  updateField={updateField}
                  allDisabled={allDisabled || disabledWhenNotUnlinked}
                  oldThreadsAccounts={oldThreadsAccounts}
                />
              )}
              <VideoReelToggle
                mediaFiles={postFormValue?.mediaFiles || []}
                selectedPlatform={selectedPlatform}
                formAccounts={formAccounts}
                updateField={updateField}
                disabled={allDisabled || disabledWhenNotUnlinked}
                currentValue={platformPost?.videoPostType || "video"}
                platformPost={platformPost}
              />
              {showVideoSelector && (
                <VideoCoverSelector
                  disabled={
                    selectedPlatform !== "LINKED" &&
                    platformFields?.[selectedPlatform]?.unlinked !== true
                  }
                  originalMediaUrl={originalMediaUrl}
                  videoUrl={videoUrl}
                  isLoading={isLoadingVideo}
                  videoMedia={videoMedia}
                  thumbnails={thumbnails}
                  updateField={updateField}
                  platforms={platforms}
                  selectedPlatform={selectedPlatform}
                  thumbnailUrl={platformPost?.thumbnailUrl}
                  videoCoverPreview={platformPost?.videoCoverPreview}
                  originalCover={
                    platformPost?.videoCoverOriginal || thumbnailUrl
                  }
                  videoCoverImageTimestamp={
                    platformPost?.videoCoverImageTimestamp
                  }
                  videoLoadingError={videoLoadingError}
                  onCoverPreviewChange={onCoverPreviewChange}
                />
              )}
              {/* Caption */}
              <CaptionField
                key={`caption-${selectedPlatform}`}
                className="flex flex-col mb-2"
                label={
                  <>
                    <div className="mr-1">Caption</div>

                    {hasYT && ["LINKED", "YT"].includes(selectedPlatform) && (
                      <div className="mr-1">(YouTube video description)</div>
                    )}
                    <PlatformUsage
                      platforms={platforms}
                      field={FIELDS.CAPTION}
                      selectedPlatform={selectedPlatform}
                    />
                  </>
                }
                selectedPlatform={selectedPlatform}
                value={platformFormValue.caption ?? ""}
                onChange={onCaptionFieldChange}
                maxLength={captionMaxLength}
                hashtagCount={captionHashtagCount}
                errors={errors}
                disabled={allDisabled || disabledWhenNotUnlinked}
                platforms={platforms}
                hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
                selectedLinkedInAccountType={selectedLinkedInAccountType}
                disableMention={selectedPlatform === "LINKED"}
                formId={bulkTab?.formId}
                captionEditHistory={captionEditHistory}
              />

              {/* First Comment */}
              {visibleFields.includes(FIELDS.FIRST_COMMENT) && (
                <PostFirstComment
                  key={`first-comment-${selectedPlatform}`}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  className="flex flex-col mb-2"
                  onChange={(val) => updateField(FIELDS.FIRST_COMMENT, val)}
                  hashtagCount={firstCommentHashtagCount}
                  errors={errors}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                  hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
                  selectedLinkedInAccountType={selectedLinkedInAccountType}
                />
              )}

              {/* URL */}
              {showUrlField && (
                <PostUrl
                  url={platformFormValue.redirectUrl}
                  checkSetting={true}
                  post={platformFormValue}
                  selectedPlatform={selectedPlatform}
                  selectedAccounts={formAccounts}
                  platforms={platforms}
                  className="mb-2"
                  title="URL"
                  onChange={(newUrl) => updateField(FIELDS.URL, newUrl)}
                  disabled={allDisabled || disabledWhenNotUnlinked}
                />
              )}

              {/* TT other fields */}
              {hasTikTokPosting && (
                <TiktokSwitchAllows
                  postFormValue={platformFormValue}
                  updateField={updateField}
                  allDisabled={allDisabled || disabledWhenNotUnlinked}
                  platforms={platforms}
                  selectedPlatform={selectedPlatform}
                  errors={errors}
                />
              )}
              {/* Threads fields */}
              {hasThreadsPosting && (
                <ThreadsReplyControl
                  key={`thReplyControlId-${selectedPlatform}`}
                  title={`Reply Control`}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  selectedAccounts={formAccounts}
                  onChange={(val) => updateField(FIELDS.TH_REPLY_CONTROL, val)}
                  errors={errors}
                  disabled={
                    allDisabled ||
                    (selectedPlatform !== "LINKED" &&
                      (!platforms || (platforms && platforms.length > 1)) &&
                      (!platformFields[selectedPlatform] ||
                        platformFields[selectedPlatform].unlinked !== true))
                  }
                />
              )}
              {/* Tags */}
              {hasPostTags && (
                <PostTags
                  hasTikTokPosting={hasTikTokPosting}
                  post={platformPost}
                  selectedPlatform={selectedPlatform}
                  platforms={platforms}
                  visibleFields={visibleFields}
                  className="flex mb-2"
                  onChange={updateField}
                  handleOpenAddAlt={handleOpenAddAlt}
                  isSummary={isSummary}
                />
              )}
            </div>
            {/* GMB Fields */}
            {visibleFields.includes(FIELDS.GMB_TOPIC_TYPE) && (
              <GmbFields
                onChange={updateFields}
                post={{
                  ...platformFormValue,
                  ...platformFields[selectedPlatform],
                }}
                selectedPlatform={selectedPlatform}
                platforms={platforms}
                className="mt-6 flex-col"
                disabled={allDisabled || disabledWhenNotUnlinked}
              />
            )}
            {/* YT Fields */}
            {hasYT && visibleFields.includes(FIELDS.YT_TITLE) && (
              <YtFields
                onChange={updateField}
                selectedAccounts={formAccounts}
                post={{
                  ...platformFormValue,
                  ...platformFields[selectedPlatform],
                }}
                selectedPlatform={selectedPlatform}
                platforms={platforms}
                className="mt-12"
                disabled={allDisabled || disabledWhenNotUnlinked}
              />
            )}
            {/* Pinterest fields */}
            {visibleFields.includes(FIELDS.PI_BOARDS) && (
              <PinterestFields
                onChange={updateField}
                selectedAccounts={formAccounts}
                post={{
                  ...platformFormValue,
                  ...platformFields[selectedPlatform],
                }}
                selectedPlatform={selectedPlatform}
                platforms={platforms}
                className="mt-6"
                disabled={allDisabled || disabledWhenNotUnlinked}
              />
            )}
          </div>

          {openAddAlt && (
            <AltTextModal
              post={platformFormValue}
              toggleAddAlt={handleOpenAddAlt}
              handleImageAltUpdate={handleImageAltUpdate}
              onSubmit={handleMediaUpdate}
            />
          )}
        </>
      )}
    </PostFormProvider>
  );
}

function getCaptionLengthLimit(post, platforms) {
  const defaultCharLimit = CHARACTER_LIMITS["FB"];
  // if post is unlinked 👉 get individual char limit
  if (post?.unlinked)
    return CHARACTER_LIMITS[post.platformType] || defaultCharLimit;
  // if not 👉 get minimum required
  else {
    const limits = platforms.map(
      (p) => CHARACTER_LIMITS[p.TYPE] || defaultCharLimit
    );

    return limits.length === 0 ? defaultCharLimit : Math.min(...limits);
  }
}

function TwitterClusterForm(props: {
  post: Post;
  unlinked: boolean | undefined;
  toggleUnlinked: () => void;
  errors?: FormValidationErrors;
  platforms: PlatformSpec[];
  formId?: string;
  captionEditHistory?: SkedEditorHistory;
  captionHashtagCount?: number;
}) {
  const {
    post: postFormValue,
    platforms,
    formId,
    captionEditHistory,
    captionHashtagCount,
  } = props;
  const captionField = useFormField("caption");
  const redirectUrlField = useFormField("redirectUrl");

  return (
    <div>
      <UnlinkToggle
        label="Customise Twitter post"
        value={props.unlinked}
        onChange={props.toggleUnlinked}
      />

      <CaptionField
        className="flex flex-col mb-2"
        label="Caption"
        value={props.post.caption ?? ""}
        onChange={captionField.input.onChange}
        maxLength={CHARACTER_LIMITS.TW}
        errors={props.errors}
        disabled={!props.unlinked}
        platforms={platforms}
        formId={formId}
        captionEditHistory={captionEditHistory}
        selectedPlatform="TW"
        hashtagCount={captionHashtagCount}
      />

      <PostUrl
        url={props.post.redirectUrl}
        checkSetting={true}
        post={postFormValue}
        className="mb-2"
        title="URL"
        onChange={redirectUrlField.input.onChange}
        disabled={!props.unlinked}
      />
    </div>
  );
}

// only used for Twitter currently (?) - fix tooltip label to refer to Twitter
function UnlinkToggle(props) {
  return (
    <div className="flex flex-col mb-4">
      <div className="flex items-center justify-start mb-2">
        <TooltipWrapper
          tooltip={`Custom changes for Twitter will apply to all Twitter accounts that you have selected.`}
        >
          <div className="text-base mr-2 font-medium">{props.label}</div>
          <Toggle
            defaultChecked={props.value}
            icons={{
              unchecked: null,
            }}
            className="active-purple"
            onChange={props.onChange}
          />
        </TooltipWrapper>
      </div>
    </div>
  );
}

// TODO move it to libs
const PostFormContext = React.createContext<{
  change: (fieldName: string, value: unknown) => void;
  formDisabled: boolean;
} | null>(null);

function PostFormProvider(props: {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const dispatch = useDispatch();

  const change = useCallback(
    (fieldName: string, value: unknown) => {
      dispatch(updatePostFormField(props.id, { [fieldName]: value }));
    },
    [dispatch, props.id]
  );

  const formContext = useMemo(
    () => ({ change, formDisabled: props.disabled }),
    [change, props.disabled]
  );

  return (
    <PostFormContext.Provider value={formContext}>
      {props.children}
    </PostFormContext.Provider>
  );
}

function useFormField(fieldName: string) {
  const formContext = useContext(PostFormContext);

  return {
    input: {
      value: "",
      onChange(value: unknown) {
        formContext?.change(fieldName, value);
      },
    },
  };
}
